// Time off for a barber or whole-shop closures (holidays), plus one-off special working hours.
import { useState } from 'react';
import { useShop } from '../app/boot.tsx';
import { useShopPaths } from '../app/nav.tsx';
import { message } from '../lib/errors.ts';
import { isoAt, shiftDate, thaiDateTime, todayLocal } from '../lib/time.ts';
import { Banner, Button, Card, Empty, Field, Loading, Page, useToast } from '../ui/index.tsx';
import { useConflictRunner } from './conflicts.tsx';
import { toMinutes, useSchedule } from './useSchedule.ts';

export function TimeOff() {
  const { api, me, isOwner } = useShop();
  const { go } = useShopPaths();
  const toast = useToast();
  const { data, reload } = useSchedule();
  const conflicts = useConflictRunner();
  const ownBarber = me.staff?.role === 'barber' ? me.staff.barberId : null;
  const [who, setWho] = useState<string>(ownBarber ?? '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [allDay, setAllDay] = useState(true);
  const [t1, setT1] = useState('10:00');
  const [t2, setT2] = useState('20:00');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!data) return <Page title="วันหยุด / ลา" back={() => go(isOwner ? '/staff/manage' : '/staff/today')}><Loading /></Page>;
  const barberName = (id: string | null) => (id ? data.barbers.find((b) => b.id === id)?.name ?? id : 'ทั้งร้าน');
  const now = Date.now();
  const upcoming = data.timeOff.filter((t) => new Date(t.endAt).getTime() > now && (isOwner || t.barberId === ownBarber));

  async function add() {
    setError(null);
    if (!from || !to) { setError('กรุณาเลือกวันที่'); return; }
    if (to < from) { setError('วันสิ้นสุดต้องไม่ก่อนวันเริ่ม'); return; }
    if (!allDay && (toMinutes(t1) === null || toMinutes(t2) === null)) { setError('กรุณากรอกเวลา'); return; }
    const startAt = isoAt(from, allDay ? '00:00' : t1);
    const endAt = allDay ? isoAt(shiftDate(to, 1), '00:00') : isoAt(to, t2);
    if (Date.parse(endAt) <= Date.parse(startAt)) { setError('เวลาสิ้นสุดต้องหลังเวลาเริ่ม'); return; }
    await conflicts.run((resolutions) => api.staff.addTimeOff({ barberId: who || null, startAt, endAt, reason: reason.trim() || undefined, resolutions }), {
      onDone: () => { setFrom(''); setTo(''); setReason(''); toast('บันทึกแล้ว', 'success'); void reload(); },
    });
  }

  async function remove(id: string) {
    try {
      await api.staff.deleteTimeOff(id);
      toast('ลบแล้ว', 'success');
      void reload();
    } catch (e) {
      toast(message(e), 'error');
    }
  }

  return (
    <Page title="วันหยุด / ลา" back={() => go(isOwner ? '/staff/manage' : '/staff/today')}>
      {error && <Banner kind="error">{error}</Banner>}
      <Card>
        <Field label="ใคร">
          <select value={who} onChange={(e) => setWho(e.target.value)} disabled={!!ownBarber}>
            {isOwner && <option value="">ทั้งร้าน (ปิดร้าน)</option>}
            {data.barbers.filter((b) => !ownBarber || b.id === ownBarber).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>
        <Field label="ตั้งแต่วันที่">
          <input type="date" value={from} min={todayLocal()} onChange={(e) => { setFrom(e.target.value); if (!to || to < e.target.value) setTo(e.target.value); }} />
        </Field>
        <Field label="ถึงวันที่">
          <input type="date" value={to} min={from || todayLocal()} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <label className="check">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
          <span>ทั้งวัน</span>
        </label>
        {!allDay && (
          <div className="row">
            <Field label="เวลาเริ่ม"><input type="time" step={300} value={t1} onChange={(e) => setT1(e.target.value)} /></Field>
            <Field label="เวลาสิ้นสุด"><input type="time" step={300} value={t2} onChange={(e) => setT2(e.target.value)} /></Field>
          </div>
        )}
        <Field label="เหตุผล (ถ้ามี)">
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="เช่น หยุดสงกรานต์" />
        </Field>
        <Button block onClick={() => void add()}>บันทึกวันหยุด/ลา</Button>
      </Card>
      <p className="section-title">กำลังจะถึง</p>
      {upcoming.length === 0 ? <Empty title="ไม่มีวันหยุดที่กำลังจะถึง" /> : (
        <div className="list">
          {upcoming.map((t) => (
            <Card key={t.id}>
              <div className="row-between">
                <div className="card-title">{barberName(t.barberId)}</div>
                <Button variant="ghost" className="btn-sm" onClick={() => void remove(t.id)}>ลบ</Button>
              </div>
              <p className="muted small">{thaiDateTime(t.startAt)} – {thaiDateTime(t.endAt)}</p>
              {t.reason && <p className="small">{t.reason}</p>}
            </Card>
          ))}
        </div>
      )}
      {conflicts.dialog}
    </Page>
  );
}

export function SpecialHours() {
  const { api } = useShop();
  const { go } = useShopPaths();
  const toast = useToast();
  const { data, reload } = useSchedule();
  const conflicts = useConflictRunner();
  const [barberId, setBarberId] = useState('');
  const [date, setDate] = useState('');
  const [start, setStart] = useState('18:00');
  const [end, setEnd] = useState('21:00');
  const [error, setError] = useState<string | null>(null);

  if (!data) return <Page title="เวลาทำงานพิเศษ" back={() => go('/staff/manage')}><Loading /></Page>;
  const bid = barberId || data.barbers[0]?.id || '';
  const barberName = (id: string) => data.barbers.find((b) => b.id === id)?.name ?? id;

  async function add() {
    setError(null);
    const s = toMinutes(start);
    const e = toMinutes(end, true);
    if (!date || s === null || e === null) { setError('กรุณากรอกวันและเวลา'); return; }
    if (e <= s) { setError('เวลาเลิกต้องหลังเวลาเริ่ม'); return; }
    try {
      await api.staff.addSpecialHours({ barberId: bid, date, startMin: s, endMin: e });
      toast('บันทึกแล้ว', 'success');
      void reload();
    } catch (err) {
      setError(message(err));
    }
  }

  return (
    <Page title="เวลาทำงานพิเศษ" back={() => go('/staff/manage')}>
      <p className="muted small" style={{ marginBottom: 12 }}>เพิ่มเวลาทำงานเฉพาะบางวัน เช่น เปิดดึกก่อนสงกรานต์ โดยไม่เปลี่ยนตารางประจำ</p>
      {error && <Banner kind="error">{error}</Banner>}
      <Card>
        <Field label="ช่าง">
          <select value={bid} onChange={(e) => setBarberId(e.target.value)}>
            {data.barbers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>
        <Field label="วันที่"><input type="date" value={date} min={todayLocal()} onChange={(e) => setDate(e.target.value)} /></Field>
        <div className="row">
          <Field label="เริ่ม"><input type="time" step={300} value={start} onChange={(e) => setStart(e.target.value)} /></Field>
          <Field label="เลิก"><input type="time" step={300} value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
        </div>
        <Button block onClick={() => void add()}>เพิ่มเวลาพิเศษ</Button>
      </Card>
      <p className="section-title">ที่ตั้งไว้</p>
      {data.special.length === 0 ? <Empty title="ยังไม่มีเวลาพิเศษ" /> : (
        <div className="list">
          {data.special.map((s) => (
            <Card key={s.id} className="row-between">
              <div>
                <div className="card-title">{barberName(s.barberId)}</div>
                <div className="muted small">{s.date} · {String(Math.floor(s.startMin / 60)).padStart(2, '0')}:{String(s.startMin % 60).padStart(2, '0')}–{String(Math.floor(s.endMin / 60)).padStart(2, '0')}:{String(s.endMin % 60).padStart(2, '0')}</div>
              </div>
              <Button variant="ghost" className="btn-sm" onClick={() => void conflicts.run((resolutions) => api.staff.deleteSpecialHours(s.id, resolutions), { onDone: () => void reload() })}>ลบ</Button>
            </Card>
          ))}
        </div>
      )}
      {conflicts.dialog}
    </Page>
  );
}
